import { useState } from 'react';
import type { RoomInfo } from '../../../hooks/useOnlineRoom';

interface Props {
  room: RoomInfo;
  onCancel: () => void;
}

export default function WaitingScreen({ room, onCancel }: Props) {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(room.room_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const typeLabel = room.game_type.charAt(0).toUpperCase() + room.game_type.slice(1);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-5">
      <div className="text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-3">
          <i className="ri-wifi-line text-3xl text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Waiting for opponent…</h2>
        <p className="text-gray-500 text-sm mt-1">Share this code with your friend</p>
      </div>

      {/* Room code — big and copy-able */}
      <div className="bg-white rounded-2xl border-2 border-dashed border-sky-300 p-8 text-center w-full max-w-sm">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Room Code</p>
        <div
          className="font-bold text-gray-800 mb-4 tracking-[0.3em] cursor-pointer select-all"
          style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '80px', letterSpacing: '0.25em' }}
          onClick={copyCode}
        >
          {room.room_code}
        </div>
        <button
          onClick={copyCode}
          className="px-5 py-2 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-500 cursor-pointer whitespace-nowrap transition-colors"
        >
          {copied
            ? <><i className="ri-check-line mr-1.5" />Copied!</>
            : <><i className="ri-file-copy-line mr-1.5" />Copy Code</>}
        </button>
      </div>

      {/* Step-by-step guide for friend */}
      <div className="bg-sky-50 rounded-xl border border-sky-100 px-6 py-4 w-full max-w-sm">
        <p className="text-xs font-bold text-sky-700 uppercase tracking-wider mb-3">
          <i className="ri-user-add-line mr-1" />Tell your friend to:
        </p>
        <ol className="space-y-2 text-sm text-sky-800">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">1</span>
            <span>Open the same website URL</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">2</span>
            <span>Click <strong>Online</strong> in the top menu</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-sky-600 text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">3</span>
            <span>Click <strong>Join Room</strong> tab → enter code <strong className="font-mono text-sky-900 tracking-widest">{room.room_code}</strong> → Join!</span>
          </li>
        </ol>
      </div>

      {/* Game info */}
      <div className="text-sm text-gray-500 text-center">
        <p className="font-medium text-gray-700">{typeLabel} · {room.difficulty.charAt(0).toUpperCase() + room.difficulty.slice(1)}</p>
        <p className="text-xs mt-0.5">Host: {room.host_name}</p>
      </div>

      {/* Pulsing indicator */}
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
        </span>
        Game will start automatically once your friend joins…
      </div>

      <button
        onClick={onCancel}
        className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-close-line mr-1" />Cancel & leave room
      </button>
    </div>
  );
}
